/**
 * Holster Finder - Cascading Filter Logic
 *
 * This custom element provides a wizard-style interface for filtering holsters
 * using Shopify's native Search & Discovery filtering system.
 *
 * Key features:
 * - Cascading dropdowns (Make → Model, Light Manufacturer → Light Model)
 * - Syncs with hidden native form for Shopify compatibility
 * - Uses FacetFiltersForm.renderPage() for AJAX updates
 * - Progressive enhancement (works without JS)
 */

class HolsterFinderForm extends HTMLElement {
  constructor() {
    super();

    // Cache DOM elements
    this.makeSelect = this.querySelector('#finder-make');
    this.modelSelect = this.querySelector('#finder-model');
    this.typeRadios = this.querySelectorAll('[name="holster-type"]');
    this.lightToggles = this.querySelectorAll('[data-light-toggle]');
    this.lightSection = this.querySelector('[data-step="4a"]');
    this.lightMakeSelect = this.querySelector('#finder-light-manufacturer');
    this.lightModelSelect = this.querySelector('#finder-light-model');
    this.resetButton = this.querySelector('.holster-finder__reset');
    this.resultCount = this.querySelector('#HolsterFinderCount');
    this.nativeForm = document.querySelector('#HolsterFinderNativeForm');

    // Section ID for AJAX requests
    this.sectionId = this.dataset.sectionId;

    // State management
    this.state = {
      selectedMake: null,
      selectedModel: null,
      selectedType: null,
      hasLight: null,
      selectedLightMake: null,
      selectedLightModel: null
    };

    // Debounced submit function
    this.debouncedSubmit = this.debounce(this.submitFilters.bind(this), 300);

    // Initialize
    this.init();
  }

  /**
   * Initialize the holster finder
   */
  init() {
    // Restore state from current filter selections
    this.restoreStateFromDOM();

    // Bind event listeners
    this.bindEvents();

    // Initialize UI based on current state
    this.updateUIFromState();
  }

  /**
   * Restore state from DOM (for page refreshes with active filters)
   */
  restoreStateFromDOM() {
    // Check Make
    if (this.makeSelect && this.makeSelect.value) {
      this.state.selectedMake = this.makeSelect.value;
    }

    // Check Model
    if (this.modelSelect) {
      const selectedModel = this.modelSelect.querySelector('option[selected]');
      if (selectedModel && selectedModel.value) {
        this.state.selectedModel = selectedModel.value;
      }
    }

    // Check Type
    const checkedType = this.querySelector('[name="holster-type"]:checked');
    if (checkedType) {
      this.state.selectedType = checkedType.value;
    }

    // Check Light filters
    if (this.lightMakeSelect && this.lightMakeSelect.value) {
      this.state.hasLight = true;
      this.state.selectedLightMake = this.lightMakeSelect.value;
    }

    if (this.lightModelSelect) {
      const selectedLightModel = this.lightModelSelect.querySelector('option[selected]');
      if (selectedLightModel && selectedLightModel.value) {
        this.state.selectedLightModel = selectedLightModel.value;
      }
    }
  }

  /**
   * Update UI based on current state
   */
  updateUIFromState() {
    // Enable model select if make is selected
    if (this.state.selectedMake && this.modelSelect) {
      this.modelSelect.disabled = false;
      this.updateModelOptions();
    }

    // Show light section if light is enabled
    if (this.state.hasLight && this.lightSection) {
      this.lightSection.classList.remove('holster-finder__step--hidden');
      this.lightToggles.forEach(toggle => {
        toggle.classList.toggle('active', toggle.dataset.lightToggle === 'yes');
      });

      // Enable light model if light make is selected
      if (this.state.selectedLightMake && this.lightModelSelect) {
        this.lightModelSelect.disabled = false;
        this.updateLightModelOptions();
      }
    }
  }

  /**
 * Restore UI elements to match current state
 */
  restoreUIState() {
    // Restore Make selection
    if (this.makeSelect && this.state.selectedMake) {
      this.makeSelect.value = this.state.selectedMake;
    }
    
    // Restore Model selection  
    if (this.modelSelect && this.state.selectedModel) {
      // We'll restore after updateModelOptions() runs
      setTimeout(() => {
        if (this.modelSelect) {
          this.modelSelect.value = this.state.selectedModel;
        }
      }, 50);
    }
    
    // Restore Type selection
    if (this.state.selectedType) {
      const typeRadio = this.querySelector(`[name="holster-type"][value="${this.state.selectedType}"]`);
      if (typeRadio) {
        typeRadio.checked = true;
      }
    }
    
    // Restore Light toggles
    if (this.state.hasLight) {
      this.lightToggles.forEach(toggle => {
        toggle.classList.toggle('active', toggle.dataset.lightToggle === 'yes');
      });
      if (this.lightSection) {
        this.lightSection.classList.remove('holster-finder__step--hidden');
      }
    }
    
    // Restore Light Manufacturer
    if (this.lightMakeSelect && this.state.selectedLightMake) {
      this.lightMakeSelect.value = this.state.selectedLightMake;
    }
    
    // Restore Light Model
    if (this.lightModelSelect && this.state.selectedLightModel) {
      setTimeout(() => {
        if (this.lightModelSelect) {
          this.lightModelSelect.value = this.state.selectedLightModel;
        }
      }, 50);
    }
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Step 1: Make selection
    if (this.makeSelect) {
      this.makeSelect.addEventListener('change', this.onMakeChange.bind(this));
    }

    // Step 2: Model selection
    if (this.modelSelect) {
      this.modelSelect.addEventListener('change', this.onModelChange.bind(this));
    }

    // Step 3: Type selection
    this.typeRadios.forEach(radio => {
      radio.addEventListener('change', this.onTypeChange.bind(this));
      
      // Allow deselecting by clicking again
      radio.addEventListener('click', (event) => {
        if (this.state.selectedType === event.target.value) {
          // Clicking the already-selected radio - deselect it
          event.target.checked = false;
          this.state.selectedType = null;
          this.debouncedSubmit();
        }
      });
    });

    // Step 4: Light toggle
    this.lightToggles.forEach(toggle => {
      toggle.addEventListener('click', this.onLightToggle.bind(this));
    });

    // Step 4a: Light manufacturer
    if (this.lightMakeSelect) {
      this.lightMakeSelect.addEventListener('change', this.onLightMakeChange.bind(this));
    }

    // Step 4b: Light model
    if (this.lightModelSelect) {
      this.lightModelSelect.addEventListener('change', this.onLightModelChange.bind(this));
    }

    // Reset button
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.onReset.bind(this));
    }
  }

  /**
   * Handle Make selection change
   */
  onMakeChange(event) {
    const selectedMake = event.target.value;
    this.state.selectedMake = selectedMake;

    if (!selectedMake) {
      // Cleared selection
      this.resetModelDropdown();
      this.state.selectedModel = null;
      this.debouncedSubmit();
      return;
    }

    // Enable model dropdown
    if (this.modelSelect) {
      this.modelSelect.disabled = false;
      this.updateModelOptions();
    }

    // Reset model selection when make changes
    this.state.selectedModel = null;

    this.debouncedSubmit();
  }

  /**
 * Update model dropdown options based on selected make
 */
    updateModelOptions() {
    if (!this.modelSelect) return;

    this.modelSelect.innerHTML = '<option value="">Select Model...</option>';

    // Get all available models from the current filter state
    const modelInputs = this.nativeForm.querySelectorAll('input[data-filter-type="model"]');

    
    modelInputs.forEach(input => {
      const count = parseInt(input.dataset.count);
      if (count > 0) {
        const option = document.createElement('option');
        option.value = input.value;
        option.textContent = input.dataset.filterLabel;
        option.dataset.param = input.name;
        this.modelSelect.appendChild(option);
      }
    });
    
    // If no models available
    if (this.modelSelect.options.length === 1) {
      this.modelSelect.innerHTML = '<option value="">No models available for this make</option>';
    }
  }

  /**
   * Reset model dropdown to initial state
   */
  resetModelDropdown() {
    if (!this.modelSelect) return;

    this.modelSelect.disabled = true;
    this.modelSelect.innerHTML = '<option value="">First select a make...</option>';
    this.state.selectedModel = null;
  }

  /**
   * Handle Model selection change
   */
  onModelChange(event) {
    this.state.selectedModel = event.target.value;
    this.debouncedSubmit();
  }

  /**
   * Handle Type (IWB/OWB/Competition) selection
   */
  onTypeChange(event) {
    this.state.selectedType = event.target.value;
    this.debouncedSubmit();
  }

  /**
   * Handle Light toggle (Yes/No)
   */
  onLightToggle(event) {
    const hasLight = event.target.dataset.lightToggle === 'yes';
    this.state.hasLight = hasLight;

    // Update UI state
    this.lightToggles.forEach(toggle => {
      toggle.classList.toggle('active', toggle.dataset.lightToggle === (hasLight ? 'yes' : 'no'));
    });

    // Show/hide light details section
    if (this.lightSection) {
      if (hasLight) {
        this.lightSection.classList.remove('holster-finder__step--hidden');
      } else {
        this.lightSection.classList.add('holster-finder__step--hidden');
        // Clear light selections
        this.state.selectedLightMake = null;
        this.state.selectedLightModel = null;
        if (this.lightMakeSelect) this.lightMakeSelect.value = '';
        this.resetLightModelDropdown();
      }
    }

    this.debouncedSubmit();
  }

  /**
   * Handle Light Manufacturer selection
   */
  onLightMakeChange(event) {
    const selectedMake = event.target.value;
    this.state.selectedLightMake = selectedMake;

    if (!selectedMake) {
      this.resetLightModelDropdown();
      this.state.selectedLightModel = null;
      this.debouncedSubmit();
      return;
    }

    // Enable light model dropdown
    if (this.lightModelSelect) {
      this.lightModelSelect.disabled = false;
      this.updateLightModelOptions();
    }

    // Reset light model selection
    this.state.selectedLightModel = null;

    this.debouncedSubmit();
  }

  /**
   * Update light model dropdown options
   */

  updateLightModelOptions() {
    if (!this.lightModelSelect) return;

    this.lightModelSelect.innerHTML = '<option value="">Select Light Model...</option>';

    // Get all available light models from the current filter state
    const lightModelInputs = this.nativeForm.querySelectorAll('input[data-filter-type="light-model"]');
    
    lightModelInputs.forEach(input => {
      const count = parseInt(input.dataset.count);
      if (count > 0) {
        const option = document.createElement('option');
        option.value = input.value;
        option.textContent = input.dataset.filterLabel;
        option.dataset.param = input.name;
        this.lightModelSelect.appendChild(option);
      }
    });
    
    // If no light models available
    if (this.lightModelSelect.options.length === 1) {
      this.lightModelSelect.innerHTML = '<option value="">No models available for this light</option>';
    }
  }

  /**
   * Reset light model dropdown
   */
  resetLightModelDropdown() {
    if (!this.lightModelSelect) return;

    this.lightModelSelect.disabled = true;
    this.lightModelSelect.innerHTML = '<option value="">First select light manufacturer...</option>';
    this.state.selectedLightModel = null;
  }

  /**
   * Handle Light Model selection
   */
  onLightModelChange(event) {
    this.state.selectedLightModel = event.target.value;
    this.debouncedSubmit();
  }

  /**
   * Reset all filters
   */
  onReset() {
    // Reset state
    this.state = {
      selectedMake: null,
      selectedModel: null,
      selectedType: null,
      hasLight: null,
      selectedLightMake: null,
      selectedLightModel: null
    };

    // Reset Make dropdown
    if (this.makeSelect) {
      this.makeSelect.value = '';
    }

    // Reset Model dropdown
    this.resetModelDropdown();

    // Reset Type radios
    this.typeRadios.forEach(radio => {
      radio.checked = false;
    });

    // Reset Light toggles
    this.lightToggles.forEach(toggle => {
      toggle.classList.remove('active');
    });

    // Hide light section
    if (this.lightSection) {
      this.lightSection.classList.add('holster-finder__step--hidden');
    }

    // Reset Light Manufacturer
    if (this.lightMakeSelect) {
      this.lightMakeSelect.value = '';
    }

    // Reset Light Model
    this.resetLightModelDropdown();

    // Submit to clear all filters (navigate to base URL)
    window.location.href = window.location.pathname;
  }

  /**
   * Submit filters to Shopify
   * Syncs custom UI state with hidden native form, then triggers submission
   */
  async submitFilters() {
  // Sync state to hidden form
  this.syncNativeFormState();

  // Build search params
  const formData = new FormData(this.nativeForm);
  const searchParams = new URLSearchParams();

  for (const [key, value] of formData.entries()) {
    const input = this.nativeForm.querySelector(`input[name="${key}"][value="${value}"]`);
    if (input && input.checked) {
      searchParams.append(key, value);
    }
  }

  const searchParamsString = searchParams.toString();
  
  // Show loading
  const productGrid = document.getElementById('ProductGridContainer');
  if (productGrid) {
    const collection = productGrid.querySelector('.collection');
    if (collection) {
      collection.classList.add('loading');
    }
  }

  try {
    // Fetch the filtered page
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParamsString}`;
    const response = await fetch(url);
    const html = await response.text();
    
    // Parse the response
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Replace product grid
    const productGridContainer = document.getElementById('ProductGridContainer');
    const newContainer = doc.getElementById('ProductGridContainer');

    if (productGridContainer && newContainer) {
      productGridContainer.innerHTML = newContainer.innerHTML;
    }
    
    // Update result count
    const newCount = doc.getElementById('HolsterFinderCount');
    const currentCount = document.getElementById('HolsterFinderCount');
    
    if (newCount && currentCount) {
      currentCount.innerHTML = newCount.innerHTML;
    }
    
    // Replace pagination
    const newPagination = doc.querySelector('.pagination-wrapper');
    const currentPagination = document.querySelector('.pagination-wrapper');

    if (newPagination && currentPagination) {
      currentPagination.innerHTML = newPagination.innerHTML;
      currentPagination.style.display = '';
    } else if (!newPagination && currentPagination) {
      currentPagination.innerHTML = '';
    }
    
    // **KEY CHANGE: Update the native form with new filter data**
  const newNativeForm = doc.getElementById('HolsterFinderNativeForm');
  if (newNativeForm && this.nativeForm) {
    this.nativeForm.innerHTML = newNativeForm.innerHTML;
  }

  // **Restore the UI state after updating the form**
  this.restoreUIState();

  // **Now update the Model dropdown with the new filtered options**
  if (this.state.selectedMake) {
    this.updateModelOptions();
  }
  // **Update Light Model dropdown if light manufacturer is selected**
  if (this.state.hasLight && this.state.selectedLightMake) {
    this.updateLightModelOptions();
  }
    
    // Update URL without reload
    const newUrl = `${window.location.pathname}?${searchParamsString}`;
    window.history.pushState({}, '', newUrl);
    
  } catch (error) {
    console.error('Filter update failed:', error);
    window.location.href = `${window.location.pathname}?${searchParamsString}`;
  } finally {
    if (productGrid) {
      const collection = productGrid.querySelector('.collection');
      if (collection) {
        collection.classList.remove('loading');
      }
    }
  }
}

  /**
   * Sync current UI state to hidden native form checkboxes
   */
  syncNativeFormState() {
    if (!this.nativeForm) return;

    // Uncheck all checkboxes first
    const allCheckboxes = this.nativeForm.querySelectorAll('input[type="checkbox"]');
    allCheckboxes.forEach(cb => {
      cb.checked = false;
    });

    // Check based on current state

    // Make filter
    if (this.state.selectedMake) {
      const makeInput = this.nativeForm.querySelector(
        `input[data-filter-type="make"][value="${CSS.escape(this.state.selectedMake)}"]`
      );
      if (makeInput) makeInput.checked = true;
    }

    // Model filter
    if (this.state.selectedModel) {
      const modelInput = this.nativeForm.querySelector(
        `input[data-filter-type="model"][value="${CSS.escape(this.state.selectedModel)}"]`
      );
      if (modelInput) modelInput.checked = true;
    }

    // Type filter
    if (this.state.selectedType) {
      const typeInput = this.nativeForm.querySelector(
        `input[data-filter-type="holster-type"][value="${CSS.escape(this.state.selectedType)}"]`
      );
      if (typeInput) typeInput.checked = true;
    }

      // Light boolean filter
    const lightInput = this.nativeForm.querySelector('input[data-filter-type="light"]');
    if (this.state.hasLight !== null) {
    // hasLight true = value "1" (Yes), hasLight false = value "0" (No)
    const lightValue = this.state.hasLight ? '1' : '0';
    const lightInput = this.nativeForm.querySelector(
      `input[data-filter-type="light"][value="${lightValue}"]`
    );
    if (lightInput) {
      lightInput.checked = true;
    }
}

    // Light Manufacturer filter
    if (this.state.hasLight && this.state.selectedLightMake) {
      const lightMakeInput = this.nativeForm.querySelector(
        `input[data-filter-type="light-manufacturer"][value="${CSS.escape(this.state.selectedLightMake)}"]`
      );
      if (lightMakeInput) lightMakeInput.checked = true;
    }

    // Light Model filter
    if (this.state.hasLight && this.state.selectedLightModel) {
      const lightModelInput = this.nativeForm.querySelector(
        `input[data-filter-type="light-model"][value="${CSS.escape(this.state.selectedLightModel)}"]`
      );
      if (lightModelInput) lightModelInput.checked = true;
    }
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Register custom element
if (!customElements.get('holster-finder-form')) {
  customElements.define('holster-finder-form', HolsterFinderForm);
}

/**
 * Re-initialize holster finder after AJAX updates
 * This ensures the finder works after FacetFiltersForm updates the page
 */
document.addEventListener('DOMContentLoaded', () => {
  // Watch for ProductGridContainer updates
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        // Update the result count if it exists
        const countElement = document.getElementById('HolsterFinderCount');
        const newCount = document.querySelector('#ProductGridContainer #HolsterFinderCount, #ProductCount span');
        if (countElement && newCount && countElement !== newCount) {
          countElement.innerHTML = newCount.innerHTML;
        }
      }
    });
  });

  const productGrid = document.getElementById('ProductGridContainer');
  if (productGrid) {
    observer.observe(productGrid, { childList: true, subtree: true });
  }
});